import { useEffect, useMemo, useState } from 'react'
import LayoutSingleView from '../components/layout/LayoutSingleView'
import Section from '../components/Section'
import { MenuItems } from '../enums/MenuItems'
import Button from '../components/Button'
import llmSettingsApi from '../api/llmSettings/LLMSettingsApi'
import Tabs from '../components/Tabs'
import { LLMSettingsName } from '../api/llmSettings/models'
import { RoleTemplateRegister } from '../uiV2/role/createRole/roleTemplates/RoleTemplateRegister'
import KernelChatStart from '@/components/kernel/KernelChatStart'

type LLMTabConfig = {
    name: LLMSettingsName
    label: string
    subtitle: string
    description: string
}

const TAB_CONFIG: LLMTabConfig[] = [
    {
        name: 'contxtScore',
        label: 'Contxt Score',
        subtitle: '✨ Contxt Score - System Prompt',
        description:
            'This prompt powers the Contxt Score evaluation that ranks candidates against an open role.',
    },
    {
        name: 'booleanQueryGenerator',
        label: 'Boolean Search Generator',
        subtitle: '🧠 Boolean Search Generator - System Prompt',
        description:
            'This prompt turns a job description into the Lucene boolean query used by the Helix search engine.',
    },
    {
        name: 'jobTitleGenerator',
        label: 'Job Title Generator',
        subtitle: '🎯 Job Title Generator - System Prompt',
        description:
            'This prompt expands input job titles into a broader title set used for job search.',
    },
    {
        name: 'roleDetailsGenerator',
        label: 'Role Creator',
        subtitle: '📝 Role Creator - System Prompt',
        description:
            'This prompt extracts the role title, location, and contract type from a natural language job description when creating a role via AI.',
    },
]

const createEmptyPromptState = (): Record<LLMSettingsName, string> =>
    Object.fromEntries(TAB_CONFIG.map((tab) => [tab.name, ''])) as Record<
        LLMSettingsName,
        string
    >

const createSavingState = (): Record<LLMSettingsName, boolean> =>
    Object.fromEntries(TAB_CONFIG.map((tab) => [tab.name, false])) as Record<
        LLMSettingsName,
        boolean
    >

const DEFAULT_BOOLEAN_JOB_DESCRIPTION =
    RoleTemplateRegister.getTemplateById('software-engineer').jobDescription

export default function LLMSettings(): React.JSX.Element {
    const [promptValues, setPromptValues] = useState(createEmptyPromptState)
    const [originalPromptValues, setOriginalPromptValues] = useState(
        createEmptyPromptState,
    )
    const [isLoading, setIsLoading] = useState(true)
    const [isSaving, setIsSaving] = useState(createSavingState)
    const [resetTarget, setResetTarget] = useState<LLMSettingsName | null>(null)
    const [error, setError] = useState<string | null>(null)
    const [activeTabIndex, setActiveTabIndex] = useState(0)
    const [booleanJobDescription, setBooleanJobDescription] = useState(
        DEFAULT_BOOLEAN_JOB_DESCRIPTION,
    )
    const [booleanQueryResult, setBooleanQueryResult] = useState('')
    const [isGeneratingBooleanQuery, setIsGeneratingBooleanQuery] =
        useState(false)
    const [generatorError, setGeneratorError] = useState<string | null>(null)

    useEffect(() => {
        const loadLLMSettings = async () => {
            try {
                setIsLoading(true)
                setError(null)

                const entries = await Promise.all(
                    TAB_CONFIG.map(async (tab) => {
                        const response = await llmSettingsApi.getLLMSettings(
                            tab.name,
                        )
                        return [tab.name, response.systemPrompt] as const
                    }),
                )

                const loadedState = {
                    ...createEmptyPromptState(),
                    ...Object.fromEntries(entries),
                } as Record<LLMSettingsName, string>

                setPromptValues(loadedState)
                setOriginalPromptValues(loadedState)
            } catch (err) {
                setError('Failed to load LLM settings. Please try again.')
                console.error('Error loading LLM settings:', err)
            } finally {
                setIsLoading(false)
            }
        }

        void loadLLMSettings()
    }, [])

    const hasChanges = (name: LLMSettingsName) =>
        promptValues[name] !== originalPromptValues[name]

    const activeResetTab = useMemo(
        () => TAB_CONFIG.find((tab) => tab.name === resetTarget) ?? null,
        [resetTarget],
    )

    const handleSave = async (name: LLMSettingsName) => {
        try {
            setIsSaving((prev) => ({ ...prev, [name]: true }))
            setError(null)

            await llmSettingsApi.saveLLMSettings({
                name,
                systemPrompt: promptValues[name],
            })

            setOriginalPromptValues((prev) => ({
                ...prev,
                [name]: promptValues[name],
            }))
        } catch (err) {
            const tabLabel =
                TAB_CONFIG.find((tab) => tab.name === name)?.label ??
                'LLM setting'
            setError(`Failed to save ${tabLabel}. Please try again.`)
            console.error('Error saving LLM settings:', err)
        } finally {
            setIsSaving((prev) => ({ ...prev, [name]: false }))
        }
    }

    const handleReset = () => {
        if (!resetTarget) {
            return
        }

        setPromptValues((prev) => ({
            ...prev,
            [resetTarget]: originalPromptValues[resetTarget],
        }))
        setResetTarget(null)
    }

    const handleGenerateBooleanQuery = async () => {
        if (!booleanJobDescription.trim()) {
            setGeneratorError('Please provide a job description.')
            return
        }

        try {
            setIsGeneratingBooleanQuery(true)
            setGeneratorError(null)

            const response =
                await llmSettingsApi.generateBooleanQueryFromJobSpec({
                    jobDescription: booleanJobDescription,
                })

            setBooleanQueryResult(response.booleanQuery.trim())
        } catch (err) {
            console.error('Error generating boolean query:', err)
            setGeneratorError(
                'Failed to generate boolean query. Please try again.',
            )
        } finally {
            setIsGeneratingBooleanQuery(false)
        }
    }

    const llmTabs = TAB_CONFIG.map((tab) => {
        const showBooleanHarness = tab.name === 'booleanQueryGenerator'

        return {
            label: tab.label,
            content: (
                <div className="mt-4 flex flex-col gap-4">
                    <div>
                        <h3 className="text-lg font-semibold">
                            {tab.subtitle}
                        </h3>
                        <p className="mt-2 text-sm text-gray-600">
                            {tab.description}
                        </p>
                    </div>

                    <textarea
                        className="w-full min-h-[800px] rounded-lg border border-gray-300 p-3 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                        placeholder="Write markdown here..."
                        value={promptValues[tab.name]}
                        onChange={(e) =>
                            setPromptValues((prev) => ({
                                ...prev,
                                [tab.name]: e.target.value,
                            }))
                        }
                    />

                    <div className="mt-3 flex justify-end gap-2">
                        <Button
                            type="button"
                            onClick={() => setResetTarget(tab.name)}
                            style="mild-danger"
                            enabled={
                                !isSaving[tab.name] && hasChanges(tab.name)
                            }
                        >
                            Reset
                        </Button>
                        <Button
                            type="button"
                            onClick={() => handleSave(tab.name)}
                            style="primary"
                            enabled={
                                !isSaving[tab.name] && hasChanges(tab.name)
                            }
                        >
                            {isSaving[tab.name] ? 'Saving...' : 'Save Changes'}
                        </Button>
                    </div>

                    {showBooleanHarness && (
                        <div className="mt-2 rounded-lg border border-gray-200 p-4">
                            <h4 className="text-md font-semibold">
                                Test Harness
                            </h4>
                            <p className="mt-1 text-sm text-gray-600">
                                Try the prompt with a live job description and
                                review the generated boolean query.
                            </p>

                            <div className="mt-4 flex flex-col gap-4 md:flex-row">
                                <div className="flex min-h-[280px] flex-1 flex-col gap-2">
                                    <label className="text-sm font-medium">
                                        Job description
                                    </label>
                                    <textarea
                                        className="h-full min-h-[240px] rounded-lg border border-gray-300 p-3 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                                        placeholder="Paste a job description here..."
                                        value={booleanJobDescription}
                                        onChange={(e) => {
                                            setBooleanJobDescription(
                                                e.target.value,
                                            )
                                            setGeneratorError(null)
                                        }}
                                    />
                                </div>

                                <div className="flex min-h-[280px] flex-1 flex-col gap-2">
                                    <label className="text-sm font-medium">
                                        Generated boolean query
                                    </label>
                                    <textarea
                                        className="h-full min-h-[240px] rounded-lg border border-gray-300 bg-gray-50 p-3 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                                        placeholder="The generated boolean query will appear here..."
                                        value={booleanQueryResult}
                                        readOnly
                                    />
                                </div>
                            </div>

                            <div className="mt-3 flex flex-wrap items-center justify-end gap-3">
                                <Button
                                    type="button"
                                    onClick={() => {
                                        setBooleanJobDescription('')
                                        setBooleanQueryResult('')
                                        setGeneratorError(null)
                                    }}
                                    style="secondary"
                                    enabled={!isGeneratingBooleanQuery}
                                >
                                    Clear
                                </Button>
                                <Button
                                    type="button"
                                    onClick={handleGenerateBooleanQuery}
                                    style="primary"
                                    enabled={
                                        !isGeneratingBooleanQuery &&
                                        booleanJobDescription.trim().length > 0
                                    }
                                >
                                    {isGeneratingBooleanQuery
                                        ? 'Generating...'
                                        : 'Generate Boolean Query'}
                                </Button>
                                {generatorError && (
                                    <div className="text-sm font-medium text-red-600">
                                        {generatorError}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            ),
        }
    })

    const tabs = [
        ...llmTabs,
        {
            label: 'Kernel',
            content: (
                <div className="mt-4">
                    <KernelChatStart />
                </div>
            ),
        },
    ]

    if (isLoading) {
        return (
            <LayoutSingleView activeMenuItem={MenuItems.LLMSettings}>
                <Section title="✨ LLM Settings">
                    <div className="flex min-h-[400px] items-center justify-center">
                        <div className="text-gray-500">Loading...</div>
                    </div>
                </Section>
            </LayoutSingleView>
        )
    }

    return (
        <LayoutSingleView activeMenuItem={MenuItems.LLMSettings}>
            <Section title="✨ LLM Settings">
                {error && (
                    <div className="mb-4 rounded-lg border border-red-400 bg-red-100 p-3 text-red-700">
                        {error}
                    </div>
                )}

                <Tabs
                    tabs={tabs}
                    activeIndex={activeTabIndex}
                    onTabChange={setActiveTabIndex}
                />

                {activeResetTab && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
                        <div className="mx-4 w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
                            <h3 className="mb-4 text-lg font-semibold">
                                Confirm Reset
                            </h3>
                            <p className="mb-6 text-gray-600">
                                Are you sure you want to reset the{' '}
                                <span className="font-semibold">
                                    {activeResetTab.label}
                                </span>{' '}
                                prompt? This will discard any unsaved changes.
                            </p>
                            <div className="flex justify-end gap-3">
                                <Button
                                    type="button"
                                    onClick={() => setResetTarget(null)}
                                    style="secondary"
                                >
                                    Cancel
                                </Button>
                                <Button
                                    type="button"
                                    onClick={handleReset}
                                    style="mild-danger"
                                >
                                    Reset
                                </Button>
                            </div>
                        </div>
                    </div>
                )}
            </Section>
        </LayoutSingleView>
    )
}
